export interface Device {
  id?: string;
  name?: string;
  model: string;
  version: string;
  firmware?: string;
  ip: string;
  rssi: number;
  hostname: string;
  mac?: string;
  lastSeen?: number;
  reportInterval?: number;
  /** EUI-64 from Zigbee2MQTT on the VCS broker (`0x` + 16 hex). */
  zigbeeIeee?: string;
  /** Zigbee link quality from Z2M state (0–255). */
  lqi?: number;
  temperature?: number;
  /** JSON shape this firmware understands (`config_version` from inventory). */
  configVersion?: number;
  /** Flash chip, partition map, and SPIFFS usage from inventory. */
  storage?: DeviceStorage;
  /** Most recently queued job (no file bodies). */
  lastTask?: DeviceLastTask | null;
  /** Matching catalog latest for this chip / product. */
  latestFirmware?: DeviceLatestFirmware | null;
  /** Last SPIFFS inventory (`path` + FNV-1a `hash`) from firmware heartbeat. */
  files?: DeviceFileInventory[];
  /** Git-style diff of that inventory against the server copies. Recomputed on each read. */
  fileDrift?: FileDrift;
  meta?: {
    hostname?: string;
    [key: string]: any;
  };
}

export interface DeviceFileInventory {
  path: string;
  size?: number | null;
  hash?: string | null;
}

export interface FileSyncRow {
  path: string;
  status: 'same' | 'different' | 'serverOnly' | 'deviceOnly' | 'unknown';
  server?: DeviceFileInventory | null;
  device?: DeviceFileInventory | null;
}

export interface FileDrift {
  reported: boolean;
  clean: boolean;
  changed: number;
  counts?: Record<string, number>;
  files: Array<{ path: string; status: FileSyncRow['status'] }>;
}

export interface FileSyncPreview {
  reported: boolean;
  files: FileSyncRow[];
  counts: Record<string, number>;
  changed: number;
  actions?: Array<{ path: string; action: 'push' | 'pull' }>;
}

export interface DevicePartition {
  label: string;
  type: string;
  subtype: string;
  address: number;
  size: number;
  running?: boolean;
}

export interface DeviceStorage {
  /** Physical flash chip size in bytes (may be larger than the partition map). */
  flashChipSize?: number;
  /** Configured / mapped flash size in bytes (end of the partition table). */
  flashSize?: number;
  sketchSize?: number;
  spiffsTotal?: number;
  spiffsUsed?: number;
  runningPartition?: string;
  partitions?: DevicePartition[];
}

export interface DeviceLastTask {
  id: string;
  command: string;
  state: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  createdAt: number;
  updatedAt: number;
  result?: string | null;
}

export interface DeviceLatestFirmware {
  environment: string;
  filename: string;
  versionString?: string | null;
  commit?: string | null;
}

export interface DashboardWidget {
  id: string;
  type: string;
  deviceId: string;
  topic: string;
  /** Humidity topic when temp and rh are published separately (atomic payloads). */
  topicRh?: string;
  /** GPS longitude topic (type gps_map). */
  topicLon?: string;
  topicSpeed?: string;
  topicAlt?: string;
  topicSat?: string;
  /** SML instantaneous power topic (type sml_meter). */
  topicPower?: string;
  /** SML reverse/export energy topic (type sml_meter). */
  topicReverse?: string;
  /** NFC tag-present topic (type nfc_tag). */
  topicPresent?: string;
  title?: string;
  /** Stacklight: definition (signal file id). One definition may run on several instances. */
  signalName?: string;
  /** Stacklight: instance id (`devices[].id` / Flow block `of`) on `hostname`. */
  stacklight?: string;
  hostname?: string;
  /** switch widget: toggle or bulb icon. */
  icon?: 'switch' | 'bulb' | string;
  /** Catalog flow that created this widget (label in the add dialog). */
  flowName?: string;
  /** Relay widget: stable catalog id (`flow:<name>:<dashId>`). */
  catalogId?: string;
  /** Relay widget: one row per wired `chN`. */
  channels?: RelayWidgetChannel[];
}

export interface RelayWidgetChannel {
  channel: number;
  name: string;
  of: string;
  key: string;
  hostname?: string;
  topic?: string;
  writable?: boolean;
}

export interface WidgetCatalogItem {
  id: string;
  deviceId: string;
  hostname: string;
  type: string;
  topic: string;
  topicRh?: string;
  topicLon?: string;
  topicSpeed?: string;
  topicAlt?: string;
  topicSat?: string;
  topicPower?: string;
  topicReverse?: string;
  topicPresent?: string;
  label: string;
  source?: string;
  flowName?: string;
  slaveId?: string;
  signalName?: string;
  /** Stacklight instance id on the controller (`devices[].id` / Flow `of`). */
  stacklight?: string;
  icon?: 'switch' | 'bulb' | string;
  catalogId?: string;
  /** Shopfloor row expanded from a relay channel. Hidden on the dashboard add list. */
  relayChannel?: boolean;
  of?: string;
  key?: string;
  channels?: RelayWidgetChannel[];
}

export interface DashboardTopicValue {
  temp: number | null;
  rh: number | null;
  value?: number | null;
  unit?: string | null;
  updatedAt: number;
  raw?: string;
  type?: string;
  id?: string;
  statusRaw?: string | null;
  statuses?: string[] | null;
}

export function displayName(device: Pick<Device, 'id' | 'hostname'>): string {
  return (device.hostname || '').trim() || device.id || 'Unknown Device';
}

/** UI-only note stored as `name` on the server. Not the device hostname. */
export function deviceComment(device: { name?: string }): string {
  return (device.name || '').trim();
}

/** Expected management check-in interval in seconds. */
export function reportIntervalSec(device: Device): number {
  const n = Number(device.reportInterval);
  if (Number.isFinite(n) && n > 0) {
    return n;
  }
  return 60;
}

/** Online until one missed check-in plus slack for the session itself. */
export function onlineTimeoutMs(device: Device): number {
  const intervalMs = reportIntervalSec(device) * 1000;
  const slackMs = Math.max(Math.floor(intervalMs * 0.25), 180000);
  return intervalMs + slackMs;
}

export function isDeviceOnline(device: Device): boolean {
  if (!device.lastSeen) {
    return false;
  }
  return Date.now() - device.lastSeen < onlineTimeoutMs(device);
}

export interface DeviceResponse {
  devices: Device[];
}

export interface ApiResponse {
  ok: boolean;
  device?: Device;
  error?: string;
}

export interface Task {
  id: string;
  deviceId: string;
  command: string;
  state: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  data?: any;
  result?: any;
  createdAt: number;
  updatedAt: number;
}

export interface TaskResponse {
  tasks: Task[];
}

export interface TaskCreateRequest {
  command: string;
  data?: any;
}

export interface TaskUpdateRequest {
  state: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  result?: any;
}

export interface Firmware {
  filename: string;
  size: number;
  created: string;
  isLatest: boolean;
  // Upload metadata (present for CI-published firmware)
  environment?: string | null;
  version?: string | null;
  commit?: string | null;
  branch?: string | null;
  buildTime?: string | null;
  uploadedAt?: string | null;
  sha256?: string | null;
  versionString?: string | null;
}

export interface FirmwareResponse {
  firmware: Firmware[];
  latest?: Firmware | null;
}

export interface StoredFileInfo {
  path: string;
  size: number;
  updatedAt: number;
}

export interface StoredFileContent extends StoredFileInfo {
  content: string;
}

export interface TemplateInfo {
  id: string;
  name: string;
  model: string;
  createdAt: number;
  updatedAt: number;
  files: StoredFileInfo[];
}

export interface DeviceFileInfo {
  id: string;
  name: string;
  hostname: string;
  model: string;
  lastSeen: number;
  files: StoredFileInfo[];
  missing: string[];
  configVersion?: number;
}

export interface FileStore {
  managed: string[];
  templates: TemplateInfo[];
  devices: DeviceFileInfo[];
}

export interface LayoutInfo {
  name: string;
  title?: string;
  path: string;
  size: number;
  updatedAt: number;
}

export type FileTreeFileKind = 'settings' | 'layout' | 'signals' | 'flow' | 'file';

export interface FileTreeFileNode {
  kind: 'file';
  name: string;
  path: string;
  fileKind: FileTreeFileKind;
  size: number;
  updatedAt: number;
  missing?: boolean;
}

export interface FileTreeFolderNode {
  kind: 'folder';
  name: string;
  path: string;
  children: FileTreeNode[];
}

export type FileTreeNode = FileTreeFileNode | FileTreeFolderNode;

export interface FileTreeResponse {
  tree: FileTreeFolderNode;
  managed: string[];
}

export interface ConfigLibraryTreeResponse {
  layouts: FileTreeFolderNode;
  flows?: FileTreeFolderNode;
  signals: FileTreeFolderNode;
}
